import { useEffect, useState } from "react";
import {
  CoinsIcon,
  PauseIcon,
  StarIcon,
  ThumbsUpIcon,
  TimerIcon,
} from "lucide-react";

import { SearchLastDecisionRow } from "./SearchLastDecisionRow";
import { WeeklyGoalRing } from "./WeeklyGoalRing";

import { Column, Row, Text } from "@/components";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { STAFFER_PET_REWARD_DETAILS } from "@/hooks/useStafferPets";
import { WEEKLY_INTEREST_GOAL, WEEKLY_SHORTLIST_GOAL } from "@/lib/constants";
import { dates } from "@/lib/dates";
import {
  SearchLastDecision,
  SearchSession,
  SearchSessionGoals,
  SearchSessionStats,
  SessionInterestCoins,
  SessionShortlist,
} from "@/types";

// Session time goal shown by the progress bar.
const SESSION_GOAL_MS = 20 * 60 * 1000;
const TICK_MS = 1_000;
const MS_PER_SECOND = 1_000;
const SECONDS_PER_MINUTE = 60;

// Seconds prove the timer is alive early on; past a couple minutes they're
// noise, so switch to minute granularity.
const SECONDS_DISPLAY_CUTOFF_MS = 2 * 60 * 1000;

const formatSessionTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / MS_PER_SECOND);
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  if (ms >= SECONDS_DISPLAY_CUTOFF_MS) return `${String(minutes)}m`;
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${String(minutes)}m ${String(seconds)}s`;
};

export const SearchSessionPanel = ({
  getSessionProgress,
  goals,
  interestCoins,
  interestCount,
  lastDecision,
  session,
  shortlists,
  stats,
}: {
  getSessionProgress: () => { activeMs: number; isIdle: boolean };
  goals: SearchSessionGoals | null;
  interestCoins: SessionInterestCoins;
  interestCount: number;
  lastDecision: SearchLastDecision | null;
  session: SearchSession | null;
  shortlists: SessionShortlist[];
  stats: SearchSessionStats | null;
}) => {
  /* React Hooks */
  const [progress, setProgress] = useState({ activeMs: 0, isIdle: false });

  /* Helpers */
  const shortlistCount = shortlists.length;
  const shortlistCoins = shortlists.reduce(
    (total, shortlist) => total + shortlist.coinsAwarded,
    0,
  );
  const shortlistGoalBonusCoins = shortlists.reduce(
    (total, shortlist) => total + shortlist.goalBonusCoins,
    0,
  );
  const goalBonusCoins = interestCoins.goalBonusCoins + shortlistGoalBonusCoins;
  const coinsEarned = shortlistCoins + interestCoins.coins + goalBonusCoins;

  useEffect(() => {
    if (!session) return;
    setProgress(getSessionProgress());
    const interval = setInterval(() => {
      setProgress(getSessionProgress());
    }, TICK_MS);
    return () => {
      clearInterval(interval);
    };
  }, [getSessionProgress, session]);

  /* Render */
  const renderTimeCard = () => {
    const percent = Math.min((progress.activeMs / SESSION_GOAL_MS) * 100, 100);
    return (
      <Column className="gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 shadow-sm">
        <Row className="items-center justify-between">
          <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-400">
            Time in search
          </Text>
          {progress.isIdle ? (
            <Row
              className="items-center gap-1"
              title="Timer paused due to inactivity"
            >
              <PauseIcon className="h-3.5 w-3.5 text-amber-500" />
              <Text className="text-[11px] font-medium text-amber-500">
                Paused
              </Text>
            </Row>
          ) : (
            <TimerIcon className="h-3.5 w-3.5 text-blue-700" />
          )}
        </Row>
        <Column className="gap-1.5">
          <Progress value={percent} />
          <Row className="items-center justify-between">
            <Row className="gap-1">
              <Text className="text-xs tabular-nums text-slate-600">
                {formatSessionTime(progress.activeMs)}
              </Text>
              <Text className="text-[10px] tabular-nums text-slate-400">
                {stats?.avgSessionTimeMs
                  ? ` (avg. ${dates.formatDuration(stats.avgSessionTimeMs)})`
                  : null}
              </Text>
            </Row>
            <Text className="text-xs font-normal text-slate-400">20m goal</Text>
          </Row>
        </Column>
      </Column>
    );
  };

  const renderCountsCard = () => {
    return (
      <Column className="gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 shadow-sm">
        <Row className="items-center justify-between">
          <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-400">
            This session
          </Text>
          {renderCoinsEarned()}
        </Row>
        <Column className="gap-0.5">
          <Row className="items-center justify-between">
            <Row className="items-center gap-1.5">
              <ThumbsUpIcon className="h-3.5 w-3.5 text-blue-700" />
              <Text className="text-xs font-normal text-slate-600">
                Interests
              </Text>
            </Row>
            <Text className="text-sm tabular-nums text-slate-700">
              {interestCount}
            </Text>
          </Row>
          {stats ? (
            <Text className="pl-5 text-[10px] font-normal tabular-nums text-slate-400">
              Last session: {stats.lastSessionInterestCount}
            </Text>
          ) : null}
        </Column>
        <Column className="gap-0.5">
          <Row className="items-center justify-between">
            <Row className="items-center gap-1.5">
              <StarIcon className="h-3.5 w-3.5 text-blue-700" />
              <Text className="text-xs font-normal text-slate-600">
                Shortlists
              </Text>
            </Row>
            <Text className="text-sm tabular-nums text-slate-700">
              {shortlistCount}
            </Text>
          </Row>
          {stats ? (
            <Text className="pl-5 text-[10px] font-normal tabular-nums text-slate-400">
              Last session: {stats.lastSessionShortlistCount}
            </Text>
          ) : null}
        </Column>
      </Column>
    );
  };

  const renderCoinsEarned = () => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="items-center gap-1">
              <CoinsIcon className="h-3.5 w-3.5 text-amber-500" />
              <Text className="text-xs tabular-nums text-slate-700">
                {coinsEarned}
              </Text>
            </Row>
          </TooltipTrigger>
          <TooltipContent align="end" side="left">
            {renderCoinsBreakdown()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderCoinRow = (label: string, coins: number, key?: string) => {
    return (
      <Row className="items-center justify-between gap-4" key={key}>
        <Text className="text-xs font-normal text-slate-600">{label}</Text>
        <Row className="items-center gap-1">
          <CoinsIcon className="h-3 w-3 text-amber-500" />
          <Text className="text-xs tabular-nums text-slate-700">{coins}</Text>
        </Row>
      </Row>
    );
  };

  const renderShortlistCoins = () => {
    if (!shortlists.length) return null;
    return (
      <Column className="gap-1">
        <Text className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
          Shortlists this session
        </Text>
        {shortlists.map((shortlist, index) =>
          renderCoinRow(
            shortlist.candidateName || "Candidate",
            shortlist.coinsAwarded,
            // Same candidate can appear once per shortlist — index keeps rows unique.
            `${shortlist.candidateName}-${String(index)}`,
          ),
        )}
      </Column>
    );
  };

  const renderInterestCoins = () => {
    if (!interestCoins.coins) return null;
    return (
      <Column className="gap-1">
        <Text className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
          Interests this session
        </Text>
        {renderCoinRow("Every 10 interests", interestCoins.coins)}
      </Column>
    );
  };

  const renderGoalBonusCoins = () => {
    if (!goalBonusCoins) return null;
    return (
      <Column className="gap-1">
        <Text className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
          Weekly goal bonuses
        </Text>
        {interestCoins.goalBonusCoins
          ? renderCoinRow("Interests goal", interestCoins.goalBonusCoins)
          : null}
        {shortlistGoalBonusCoins
          ? renderCoinRow("Shortlists goal", shortlistGoalBonusCoins)
          : null}
      </Column>
    );
  };

  const renderCoinsBreakdown = () => {
    if (!coinsEarned) {
      return (
        <Text className="text-xs font-normal text-slate-500">
          Shortlist candidates to earn coins. Every 10 interests earns 1 too.
        </Text>
      );
    }
    return (
      <Column className="gap-2">
        {renderShortlistCoins()}
        {renderInterestCoins()}
        {renderGoalBonusCoins()}
      </Column>
    );
  };

  const renderGoalsCard = () => {
    if (!goals) return null;
    // Week-to-date counts snapshot at session start, plus what this session
    // has added since. The server pays the coin bonus when a count reaches
    // its goal; the ring only mirrors that.
    return (
      <Column className="gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 shadow-sm">
        <Text className="font-plex-mono text-[11px] font-medium uppercase tracking-wider text-slate-400">
          Weekly goals
        </Text>
        <Row className="justify-around">
          <WeeklyGoalRing
            bonusCoins={STAFFER_PET_REWARD_DETAILS.WEEKLY_INTEREST_GOAL.amount}
            colorClass="stroke-violet-500"
            goal={WEEKLY_INTEREST_GOAL}
            label="Interests"
            noun="interests"
            perItemNote="Every 10 search interests is also worth 1 coin."
            value={goals.weeklyInterestCount + interestCount}
          />
          <WeeklyGoalRing
            bonusCoins={STAFFER_PET_REWARD_DETAILS.WEEKLY_SHORTLIST_GOAL.amount}
            colorClass="stroke-primary-500"
            goal={WEEKLY_SHORTLIST_GOAL}
            label="Shortlists"
            noun="shortlists"
            value={goals.weeklyShortlistCount + shortlistCount}
          />
        </Row>
      </Column>
    );
  };

  const render = () => {
    if (!session) return null;
    return (
      <Column className="xl:w-70 hidden w-60 gap-4 xl:flex 2xl:w-80">
        {renderTimeCard()}
        <SearchLastDecisionRow lastDecision={lastDecision} />
        {renderCountsCard()}
        {renderGoalsCard()}
      </Column>
    );
  };

  return render();
};
